import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Slide,
  CircularProgress,
  Link,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useTranslate } from 'src/locales';
import { TransitionProps } from '@mui/material/transitions';
import { executeUserActions } from 'src/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import i18next from 'i18next';
import { CONFIG } from 'src/config-global';
import { toast } from 'src/components/snackbar';
import { WordViewer } from './word-viewer';

const Transition = React.forwardRef<unknown, TransitionProps & { children: React.ReactElement }>(
  (p, r) => <Slide direction="up" ref={r} {...p} />
);

const upload = async (container: string, files: File[], payload: any) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  fd.append('payload', JSON.stringify(payload));
  const { data } = await axios.post(
    `${CONFIG.baseUrl}/file/upload_file_to_storage/${container}`,
    fd,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return data.results || [];
};

const fetchBlob = async (m: any) => {
  const { data } = await axios.post(
    `${CONFIG.baseUrl}/property/get-images`,
    {
      tk: EncryptUtils.encryptedData({ FileContentID: m.FileContentID, blobName: m.BlobName }),
    },
    { responseType: 'blob' }
  );
  return data as Blob;
};

const removeBlobs = async (meta: any[], sql: any, u: string) => {
  if (!meta.length) return true;
  await Promise.all(
    meta.map((m) =>
      axios.post(`${CONFIG.baseUrl}/property/delete-image`, {
        tk: EncryptUtils.encryptedData({
          containerName: m.containerName,
          blobName: m.blobName,
          FileContentId: m.fileContentId,
          sql_data: sql,
        }),
      })
    )
  );
  toast.success(`${u} successful`);
  return true;
};

const deleteBlobs = async (items: any, sql: any, toastLabel = 'Delete') => {
  if (!items.length) return true;

  await axios.post(`${CONFIG.baseUrl}/file/delete-blobs`, {
    tk: EncryptUtils.encryptedData({
      items, // <-- array of blobs to delete
      sql_data: sql, // still pass the procedure payload
    }),
  });

  toast.success(`${toastLabel} successful`);
  return true;
};

const group = <T, K extends keyof any>(arr: T[], fn: (v: T) => K) =>
  arr.reduce<Record<K, T[]>>(
    (a, v) => {
      const k = fn(v);
      (a[k] = a[k] || []).push(v);
      return a;
    },
    {} as Record<K, T[]>
  );

const RequirementUploader: React.FC<any> = ({
  uiData,
  disabled,
  module_name,
  paramsData = {},
  containerName,
  isAdmin,
  defaultData,
  bannerDetails,
}) => {
  const { t } = useTranslate('form');
  const [rows, setRows] = useState<any[]>([]);
  const [fileDlg, setFileDlg] = useState(false);
  const [currMeta, setCurrMeta] = useState<any[]>([]);
  const [currId, setCurrId] = useState<number | null>(null);
  const [viewer, setViewer] = useState<{ blob: Blob; name: string } | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [full, setFull] = useState(false);
  const [loadingFileIdx, setLoadingFileIdx] = useState<number | null>(null);
  const [loadingRows, setLoadingRows] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoadingRows(true);
      if (!uiData?.ui_rows_sql_data) {
        setLoadingRows(false);
        return;
      }

      // 1) fetch the table rows
      const base = await executeUserActions(module_name, {
        tk: EncryptUtils.encryptedData({ sql_data: uiData.ui_rows_sql_data }),
      });
      if (!base.success) {
        toast.error('Failed to load requirements.');
        setLoadingRows(false);
        return;
      }

      // determine which RecordId to use
      const RelatedRecordId = isAdmin
        ? paramsData?.RecordId
        : defaultData.RecordId || bannerDetails?.FullApplicationRecordId;

      // 2) optionally fetch existing file-metadata, but don’t let failures abort us
      let stored: Record<string, any[]> = {};
      if (uiData.get_files_sql_data && RelatedRecordId) {
        try {
          const fr = await executeUserActions(module_name, {
            tk: EncryptUtils.encryptedData({
              sql_data: uiData.get_files_sql_data,
              RelatedRecordId,
            }),
          });
          if (fr.success) {
            stored = group(
              fr.data.flat().filter((r: any) => typeof r === 'object'),
              (x: any) => x.Label ?? x.RequirementCode ?? x.Code
            );
          } else {
            toast.warning('Could not fetch file metadata, proceeding without it.');
          }
        } catch (err) {
          console.error('metadata fetch error', err);
          toast.warning('Error fetching file metadata, proceeding without it.');
        }
      }
      // console.log('stored', stored);

      // 3) build your rows array exactly as before
      const list = base.data
        .flat()
        .filter((r: any) => r && (r.Label || r.label))
        .map((r: any, i: number) => {
          const label = r.Label ?? r.label;
          const code = r.code;
          const meta = stored[label] || [];
          const types = (() => {
            try {
              return Array.isArray(r.AcceptFileTypes)
                ? r.AcceptFileTypes
                : JSON.parse(r.AcceptFileTypes || '[]');
            } catch {
              return (r.AcceptFileTypes || '').split(',').filter(Boolean);
            }
          })();
          ['en', 'es'].forEach((lng) =>
            i18next.addResourceBundle(
              lng,
              'form',
              {
                [label]: r[`Description${lng === 'en' ? 'En' : 'Es'}`] || '',
                [`${label}_level`]: r[`RequiredLevel${lng === 'en' ? 'EN' : 'ES'}`] || '',
              },
              true,
              true
            )
          );
          return {
            id: i + 1,
            label,
            code,
            levelKey: `${label}_level`,
            meta,
            status: meta.length ? 'Uploaded' : 'Not Uploaded',
            acceptFileTypes: types,
            reDirectText: r.RedirectLinkText,
            reDirectLink: r.RedirectLink,
          };
        });

      // 4) always setRows, even if metadata failed
      setRows(list);
      setLoadingRows(false);
    } catch (err) {
      setLoadingRows(false);
      console.error('Unexpected load error', err);
      toast.error('An unexpected error occurred loading the table.');
    }
  }, [
    uiData?.ui_rows_sql_data,
    uiData?.get_files_sql_data,
    paramsData?.RecordId,
    defaultData.RecordId,
    bannerDetails?.FullApplicationRecordId,
    module_name,
    isAdmin,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    try {
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const pick = (id: number, re = false) => {
    const row = rows.find((x) => x.id === id);
    if (!row) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = row.acceptFileTypes.length
      ? row.acceptFileTypes.map((x: string) => `.${x}`).join(',')
      : '*/*';
    input.multiple = true;
    input.onchange = async () => {
      if (!input.files?.length) return;
      const files = Array.from(input.files);
      setRows((p) => p.map((x) => (x.id === id ? { ...x, status: 'Uploading' } : x)));
      try {
        const RelatedRecordId = isAdmin
          ? paramsData?.RecordId
          : defaultData.RecordId || bannerDetails?.FullApplicationRecordId;

        if (re && row.meta.length) await deleteBlobs(row.meta, uiData.delete_files_sql_data);

        const payload = {
          sql_data: uiData.upload_files_sql_data,
          RequirementCode: row.code,
          RelatedRecordId: RelatedRecordId,
        };
        await upload(containerName || 'parkcity-source-files', files, payload);
        toast.success('Upload successful');
        setRows((p) => p.map((x) => (x.id === id ? { ...x, status: 'Uploaded' } : x)));
      } catch {
        toast.error('Upload failed');
        setRows((p) => p.map((x) => (x.id === id ? { ...x, status: 'Not Uploaded' } : x)));
      } finally {
        await refresh();
      }
    };
    if (re && row.meta.length) {
      if (window.confirm('Delete existing files?')) input.click();
    } else input.click();
  };

  const clear = async (id: number) => {
    const row = rows.find((x) => x.id === id);
    if (!row?.meta.length) return;
    if (!window.confirm('Delete all files?')) return;
    await deleteBlobs(row.meta, uiData.delete_files_sql_data);
    // setRows((p) => p.map((x) => (x.id === id ? { ...x, meta: [], status: 'Not Uploaded' } : x)));
    await refresh();
  };

  const delSingle = async (idx: number) => {
    if (currId == null) return;
    const row = rows.find((x) => x.id === currId);
    if (!row) return;
    const tgt = row.meta[idx];
    await deleteBlobs([tgt], uiData.delete_files_sql_data);
    // const nm = row.meta.filter((_: any, i: number) => i !== idx);
    // setRows((p) =>
    //   p.map((x) =>
    //     x.id === currId ? { ...x, meta: nm, status: nm.length ? 'Uploaded' : 'Not Uploaded' } : x
    //   )
    // );
    // setCurrMeta(nm);

    await refresh();

    // keep dialog list in sync
    setCurrMeta((p) => p.filter((_, i) => i !== idx));
  };

  const cols: GridColDef[] = [
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      renderCell: (p) => (
        <Box
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.5,
            padding: '8px',
          }}
        >
          <Typography component="span" sx={{ display: 'inline' }}>
            {t(`form:${p.row.label}`)}{' '}
            {p.row.reDirectLink && p.row.reDirectText ? (
              <Link
                href={p.row.reDirectLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'inline', color: 'error.main' }}
              >
                {p.row.reDirectText}
              </Link>
            ) : (
              ''
            )}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'level',
      headerName: 'Level',
      flex: 1,
      renderCell: (p) => (
        <Box
          sx={{
            width: '100%',
            height: 1,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {t(`form:${p.row.levelKey}`)}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (p) => (
        <Box
          sx={{
            width: '100%',
            height: 1,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {p.value === 'Uploading' && <CircularProgress size={18} />} {p.value}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 3,
      minWidth: 220,
      cellClassName: 'stickyActions',
      headerClassName: 'stickyActionsHeader',
      renderCell: (p: GridRenderCellParams<any, any>) => {
        const r = p.row;
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 1,
              height: 1,
            }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon icon="mdi:cloud-arrow-up" />}
                onClick={() => pick(r.id)}
                disabled={disabled || r.status === 'Uploading'}
              >
                {r.status === 'Not Uploaded' ? 'Upload' : 'Add More'}
              </Button>
              {r.meta.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Icon icon="mdi:cloud-refresh" />}
                    onClick={() => pick(r.id, true)}
                    disabled={disabled || r.status === 'Uploading'}
                  >
                    Re-upload
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Icon icon="mdi:delete" />}
                    onClick={() => clear(r.id)}
                    disabled={disabled || r.status === 'Uploading'}
                  >
                    Clear Files
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Icon icon="mdi:eye" />}
                    onClick={() => {
                      setCurrMeta(r.meta);
                      setCurrId(r.id);
                      setFileDlg(true);
                    }}
                    disabled={disabled || r.status === 'Uploading'}
                  >
                    View Files
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        );
      },
    },
  ];

  const openViewer = async (m: any, idx: number) => {
    setLoadingFileIdx(idx);
    try {
      const blob = await fetchBlob(m);
      setViewer({ blob, name: m.FileName || 'file' });
      setViewOpen(true);
      setFull(false);
    } catch (error) {
      console.error(error);
      toast.error('Preview failed');
    } finally {
      setLoadingFileIdx(null);
    }
  };

  const downloadBlob = async (m: any) => {
    try {
      const blob = await fetchBlob(m);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = m.FileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error('Download failed');
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        {t(uiData?.section_label) || 'Requirements Upload'}
      </Typography>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 900 }}>
          <DataGrid
            rows={rows}
            columns={cols}
            getRowId={(r) => r.id}
            loading={loadingRows}
            disableRowSelectionOnClick
            autoHeight
            getRowHeight={() => 'auto'}
            hideFooterPagination={!uiData.show_pagination}
            hideFooter={!uiData.show_pagination}
            sx={{
              '& .stickyActions': {
                position: 'sticky',
                right: 0,
                zIndex: 2,
              },
              '& .stickyActionsHeader': {
                position: 'sticky',
                right: 0,
                zIndex: 3,
              },
            }}
          />
        </Box>
      </Box>
      <Dialog open={fileDlg} onClose={() => setFileDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Uploaded Files
          <IconButton
            aria-label="close"
            onClick={() => setFileDlg(false)}
            sx={(th) => ({ position: 'absolute', right: 8, top: 8, color: th.palette.grey[500] })}
          >
            <Icon icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List>
            {currMeta.map((m, i) => {
              // split name and ext
              const fn = m.FileName || '';
              const dot = fn.lastIndexOf('.');
              const namePart = dot > 0 ? fn.slice(0, dot) : fn;
              const extPart = dot > 0 ? fn.slice(dot) : '';
              const isLoading = loadingFileIdx === i;

              return (
                <ListItem
                  key={i}
                  secondaryAction={
                    <Box display="flex" gap={1}>
                      {/* download & delete icons */}
                      <Box display="flex" gap={1}>
                        <IconButton
                          edge="end"
                          aria-label="download"
                          onClick={() => downloadBlob(m)}
                        >
                          <Icon icon="mdi:download" />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete" onClick={() => delSingle(i)}>
                          <Icon icon="mdi:delete" />
                        </IconButton>
                      </Box>
                    </Box>
                  }
                  disablePadding
                >
                  <ListItemButton onClick={() => openViewer(m, i)} disabled={isLoading}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        pr: (theme) => theme.spacing(6),
                      }}
                    >
                      {/* name truncated, extension always visible */}
                      <Box
                        component="span"
                        sx={{
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {namePart}
                      </Box>
                      <Box component="span" sx={{ flexShrink: 0, ml: 0.5 }}>
                        {extPart}
                      </Box>
                      {isLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={full}
        TransitionComponent={Transition}
      >
        <DialogTitle>
          File Viewer
          <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 1 }}>
            <IconButton onClick={() => setFull((p) => !p)}>
              <Icon icon={full ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'} />
            </IconButton>
            <IconButton onClick={() => setViewOpen(false)}>
              <Icon icon="mdi:close" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ height: full ? '100%' : 'auto' }}>
          {viewer && (
            <>
              <Typography variant="subtitle1" gutterBottom noWrap>
                {viewer.name}
              </Typography>
              {(() => {
                const url = URL.createObjectURL(viewer.blob);
                const typ = viewer.blob.type;
                if (typ.startsWith('image/'))
                  return (
                    <img
                      src={url}
                      alt="preview"
                      style={{
                        width: '100%',
                        maxHeight: full ? '100%' : 600,
                        objectFit: 'contain',
                      }}
                    />
                  );
                if (typ === 'application/pdf')
                  return (
                    <iframe
                      src={url}
                      style={{ width: '100%', height: full ? '100%' : 600 }}
                      title="pdf"
                    />
                  );
                if (typ.includes('excel') || typ.includes('spreadsheet') || typ.includes('csv'))
                  return (
                    <Typography>
                      Spreadsheets <strong>.csv, .xls, .xlsx</strong> files aren&rsquo;t supported
                      in the inline viewer.&nbsp;
                      <Button onClick={() => window.open(url, '_blank')}>Download</Button>
                    </Typography>
                  );
                if (
                  typ === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ) {
                  return <WordViewer blob={viewer.blob} />;
                }
                if (typ.includes('application/msword'))
                  return (
                    <Typography>
                      Legacy <strong>.doc</strong> files aren&rsquo;t supported in the inline
                      viewer.&nbsp;
                      <Button onClick={() => window.open(url, '_blank')}>Download</Button>
                    </Typography>
                  );
                return <Typography>No preview</Typography>;
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequirementUploader;
